/* SEO autopilot panel — pure view helpers (owner 25-10-2026: "the settings ui is 4th grade").
 * DOM-free so it runs under `node --test --experimental-strip-types`. Presentation only: every
 * value comes from GET /v1/content/seo-autopilot and every change still goes through
 * settingsError / settingsPatch to the unchanged POST endpoints. */
import { isArchiveId } from '../../../data/seo-autopilot-model.ts';
import type { SeoAutopilotCadence, SeoAutopilotListing, SeoAutopilotSchedule, SeoAutopilotSettings } from '../../../data/seo-autopilot-model.ts';

export type PageKind = 'listing' | 'archive';

/** Listing vs archive/category page, from the target URL path. */
export function pageKind(url: string | null | undefined, id?: string): PageKind {
  if (id && isArchiveId(id)) return 'archive'; // category x city target (seo lane 6405cc7)
  const path = (/^(?:https?:\/\/[^/]+)?(\/[^?#]*)?/i.exec((url ?? '').trim())?.[1] ?? '').toLowerCase();
  const segs = path.split('/').filter(Boolean);
  if (segs.some((x) => ['category', 'categories', 'tag', 'tags', 'archive', 'archives'].includes(x))) return 'archive';
  if (segs.length === 1 && ['places', 'events', 'sessions', 'listings', 'services'].includes(segs[0])) return 'archive';
  return 'listing';
}

/** Short path label for a page URL. */
export function pagePath(url: string | null | undefined): string {
  const m = /^(?:https?:\/\/)?(?:www\.)?([^/?#]+)([^?#]*)/i.exec((url ?? '').trim());
  if (!m) return '';
  const p = m[2].replace(/\/+$/, '');
  return p === '' ? m[1] : p;
}

export function scheduleToday(schedule: SeoAutopilotSchedule | undefined, todayIso: string): number {
  const d = schedule?.preview.find((x) => x.date === todayIso);
  return d ? d.items.length : 0;
}

export type PageState = 'active' | 'paused' | 'auto_paused' | 'not_eligible';
export function pageState(l: SeoAutopilotListing, s: SeoAutopilotSettings): PageState {
  if (!l.eligible) return 'not_eligible';
  if (s.paused_listings.includes(l.listing_id) || (l.tenant_slug !== '' && s.paused_tenants.includes(l.tenant_slug))) return 'paused';
  if (l.auto_paused) return 'auto_paused';
  return 'active';
}
export const PAGE_STATE_LABEL: Record<PageState, string> = { active: 'Active', paused: 'Paused by you', auto_paused: 'Auto-paused', not_eligible: 'Not eligible' };

export type PageSort = 'page' | 'tenant' | 'keyword' | 'position' | 'next';
/** listing_id -> first date in the 7-day schedule. */
export function nextDraftDates(schedule: SeoAutopilotSchedule | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const d of schedule?.preview ?? []) for (const it of d.items) if (!(it.listing_id in out)) out[it.listing_id] = d.date;
  return out;
}

export function filterSortPages(
  rows: SeoAutopilotListing[], s: SeoAutopilotSettings, opts: { q: string; state: PageState | 'all'; sort: PageSort; dir: 'asc' | 'desc' }, next: Record<string, string>,
): SeoAutopilotListing[] {
  const q = opts.q.trim().toLowerCase();
  const out = rows.filter((l) => {
    if (opts.state !== 'all' && pageState(l, s) !== opts.state) return false;
    if (!q) return true;
    return [l.listing_title, l.tenant_slug, l.keyword ?? '', l.listing_url ?? ''].some((v) => v.toLowerCase().includes(q));
  });
  const val = (l: SeoAutopilotListing): string | number | null => {
    switch (opts.sort) {
      case 'tenant': return l.tenant_slug.toLowerCase();
      case 'keyword': return (l.keyword ?? '').toLowerCase();
      case 'position': return typeof l.position === 'number' ? l.position : null;
      case 'next': return next[l.listing_id] ?? null;
      default: return l.listing_title.toLowerCase();
    }
  };
  return out.slice().sort((a, b) => {
    const va = val(a); const vb = val(b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1; // blanks last, both directions
    if (vb === null) return -1;
    const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
    return opts.dir === 'asc' ? c : -c;
  });
}

/** Plain-English labels + helper text for the settings form. */
export const CADENCE_PLAIN: Record<keyof SeoAutopilotCadence, { label: string; help: string; unit: string; advanced?: boolean }> = {
  not_ranking_per_week: { label: 'Pages not on Google’s first page', help: 'Ranking worse than 10th, or not ranking yet. These need the most help.', unit: 'drafts a week' },
  band_4_10_per_week: { label: 'Pages ranking 4th to 10th', help: 'On page one but below the top three.', unit: 'drafts a week' },
  band_2_3_per_week: { label: 'Pages ranking 2nd or 3rd', help: 'Close to the top. A lighter touch keeps them there.', unit: 'drafts a week' },
  top1_interval_weeks: { label: 'Pages holding #1', help: 'One fresh draft every few weeks so the page stays current.', unit: 'weeks between drafts' },
  top1_hold_checks: { label: 'Weeks at #1 before slowing down', help: 'How many weekly checks a page must hold #1 before it moves to the slow rate.', unit: 'weekly checks', advanced: true },
  step_up_checks: { label: 'Weeks below 3rd before speeding up', help: 'How many weekly checks a page must sit below 3rd before it gets drafts more often again.', unit: 'weekly checks', advanced: true },
  autopause_weeks: { label: 'Weeks with no improvement before pausing a page', help: 'At the top rate, pause a page that has not moved after this many weeks.', unit: 'weeks', advanced: true },
  archive_per_week: { label: 'Category pages (category + city)', help: 'Each indexed category page for a city, for example Reiki in London.', unit: 'drafts a week' },
  guard_reject_pause_after: { label: 'Rejected drafts in a row before pausing a page', help: 'If the quality check rejects this many drafts in a row, the page pauses until you resume it.', unit: 'rejections', advanced: true },
};

export const ALERT_PLAIN: Record<string, string> = {
  guard_rejected: 'Quality check rejected a draft',
  auto_paused: 'A page was paused automatically',
  unresolved_placeholder: 'A draft had an unfilled placeholder',
  cap_reached: 'A volume cap was reached',
  failed: 'A draft could not be written',
};
export function alertLabel(type: string): string {
  return ALERT_PLAIN[type] ?? (type ? type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ') : 'Notice');
}

/** Split an alert detail into its quoted sentence (if any) and the rest, so the UI can show the sentence as a quote. */
export function splitQuote(detail: string): { quote: string | null; rest: string } {
  const m = /["“]([^"”]{3,})["”]/.exec(detail);
  if (!m) return { quote: null, rest: detail };
  return { quote: m[1], rest: (detail.slice(0, m.index) + detail.slice(m.index + m[0].length)).replace(/\s{2,}/g, ' ').trim() };
}

/** 'YYYY-MM-DD' -> 'Mon 26 Oct' from numbers only (no string Date parsing). */
export function dayLabel(iso: string, day?: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day ? day + ' ' : ''}${Number(m[3])} ${months[Number(m[2]) - 1] ?? ''}`.trim();
}

// ---- Activity copy in plain English (Delta UI IVG R2) -------------------------------

const ALERT_PHRASES: Array<[RegExp, string]> = [
  [/^unverifiable claim\b/i, 'Possible unverifiable claim'],
  [/^unsupported (statistic|stat)\b/i, 'Possible made-up statistic'],
  [/^medical claim\b/i, 'Possible medical claim'],
  [/^guarantee\b/i, 'Possible promise of results'],
];

/** One plain sentence + the quoted text (if any) for an alert detail. Internal guard codes
 *  (e.g. "A10", "G3") are dropped, a dangling colon left by the lifted quote is removed, and a
 *  trailing location ("(paragraph 2)", "in the intro") becomes "— paragraph 2". */
export function describeAlert(detail: string): { sentence: string; quote: string | null } {
  const { quote } = splitQuote(detail);
  let lead = detail; let tail = '';
  const m = /["“]([^"”]{3,})["”]/.exec(detail);
  if (m) { lead = detail.slice(0, m.index); tail = detail.slice(m.index + m[0].length); }
  lead = lead.replace(/^\s*[A-Z]{1,2}\d{1,3}\b[\s:–—-]*/, '').replace(/[\s:–—-]+$/, '').trim();
  for (const [re, plain] of ALERT_PHRASES) { if (re.test(lead)) { lead = lead.replace(re, plain); break; } }
  tail = tail.replace(/^[\s:,.–—-]+/, '').replace(/^\((.*)\)$/, '$1').replace(/[\s.]+$/, '').trim();
  lead = lead.replace(/_/g, ' ');
  const sentence = (lead ? lead.charAt(0).toUpperCase() + lead.slice(1) : 'Notice') + (tail ? ` — ${tail}` : '');
  return { sentence, quote };
}

const CHANGE_KEYS: Record<string, string> = {
  cadence: 'posting frequency', caps: 'volume limits', paused_listings: 'paused pages', paused_tenants: 'paused partners',
  global_stop: 'the on/off switch', full_auto: 'automatic publishing', target_id: 'destination site',
};
/** "human:owner · cadence" -> "You changed: posting frequency". */
export function describeChange(principal: string, keys: string[]): string {
  const who = principal.startsWith('human:') ? 'You' : /autopilot|system|cron/i.test(principal) ? 'The autopilot' : 'Someone';
  const what = keys.map((k) => CHANGE_KEYS[k] ?? k.replace(/_/g, ' ')).join(', ') || 'settings';
  return `${who} changed: ${what}`;
}

/** Plain words for an eligibility / auto-pause reason code. */
export function reasonLabel(code: string | null | undefined): string {
  const map: Record<string, string> = {
    no_keyword: 'No keyword yet', not_synced: 'Page not synced yet', guard_rejected_3_in_a_row: 'Quality check rejected 3 drafts in a row',
    no_improvement: 'No ranking improvement', not_indexable: 'Page not indexable', paused: 'Paused',
  };
  if (!code) return 'Not eligible';
  return map[code] ?? code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, ' ');
}
